import { Plugin } from 'prosemirror-state';

/**
 * Creates a ProseMirror plugin that handles text direction and alignment.
 * @param {string} defaultDirection - Default direction for empty paragraphs ('ltr' or 'rtl')
 * @param {boolean} autoDetectLanguage - When true, auto-sets RTL/alignment for Hebrew text
 * @returns {Plugin}
 */
function createAutoDirectionPlugin(
  defaultDirection = 'ltr',
  autoDetectLanguage = true
) {
  const defaultAlign = defaultDirection === 'rtl' ? 'right' : 'left';
  const isRTLView = defaultDirection === 'rtl';
  let previousDirection = null;
  let previousAlign = null;
  function containsHebrew(text) {
    return /[\u0590-\u05ff]/.test(text);
  }

  return new Plugin({
    appendTransaction(transactions, oldState, newState) {
      const docChanged = transactions.some(tr => tr.docChanged);
      if (!docChanged) return null;

      let tr = newState.tr;
      let modified = false;

      newState.doc.descendants((node, pos) => {
        if (node.type.name === 'paragraph') {
          const textContent = node.textContent;
          const isEmpty = !textContent || textContent.trim() === '';
          const currentDirection = isRTLView
            ? defaultDirection
            : node.attrs.direction || defaultDirection;
          const isManual = node.attrs.autoDetected === false;

          let detectedDirection = currentDirection;
          let needsDirectionUpdate = false;

          let needsAlignUpdate = false;
          let detectedAlign = node.attrs.align;

          if (!isManual) {
            const isHebrew = autoDetectLanguage && containsHebrew(textContent);
            const autoDetectDirection = isHebrew ? 'rtl' : currentDirection;
            const autoDetectAlign = isHebrew ? 'right' : node.attrs.align;

            detectedDirection = isEmpty
              ? previousDirection || currentDirection || defaultDirection
              : autoDetectDirection;
            needsDirectionUpdate = detectedDirection !== currentDirection;

            const newAlign = isEmpty
              ? previousAlign || node.attrs.align || defaultAlign
              : autoDetectAlign;

            if (newAlign && newAlign !== node.attrs.align) {
              detectedAlign = newAlign;
              needsAlignUpdate = true;
            }
          }

          if (needsDirectionUpdate || needsAlignUpdate) {
            tr.setNodeMarkup(pos, null, {
              ...node.attrs,
              align: detectedAlign,
              direction: detectedDirection,
              autoDetected: needsAlignUpdate ? true : node.attrs.autoDetected,
            });
            modified = true;
          }

          if (isManual) {
            previousDirection = currentDirection;
            previousAlign = node.attrs.align;
          } else if (!isEmpty) {
            previousDirection = needsDirectionUpdate
              ? detectedDirection
              : currentDirection;
            previousAlign = needsAlignUpdate ? detectedAlign : node.attrs.align;
          }
        }
      });

      return modified ? tr : null;
    },
  });
}

export default createAutoDirectionPlugin;
